import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Header } from "@/components/Header";
import ScrollToTop from "@/components/ScrollToTop";
import { ChatWidget } from "@/components/chat/ChatWidget";

// Eagerly load critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";

// Lazy load non-critical pages
const Catalog = lazy(() => import("./pages/Catalog"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Thanks = lazy(() => import("./pages/Thanks"));
const Admin = lazy(() => import("./pages/Admin"));
const Requests = lazy(() => import("./pages/Requests"));
const Profile = lazy(() => import("./pages/Profile"));
const CompanyDetails = lazy(() => import("./pages/CompanyDetails"));
const ApiDocumentation = lazy(() => import("./pages/ApiDocumentation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmbedChatbot = lazy(() => import("./pages/EmbedChatbot"));
const ExportDatastreams = lazy(() => import("./pages/ExportDatastreams"));
const ForDevelopers = lazy(() => import("./pages/ForDevelopers"));
const DevDatastreams = lazy(() => import("./pages/DevDatastreams"));
const DevDocumentation = lazy(() => import("./pages/DevDocumentation"));
const PriceCatalog = lazy(() => import("./pages/PriceCatalog"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const AppContent = () => {
  const location = useLocation();
  const isEmbedRoute = location.pathname === '/embed/chatbot';

  return (
    <>
      <Toaster />
      <Sonner />
      <ScrollToTop />
      {!isEmbedRoute && <Header />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/" element={<Index />} />
          <Route path="/embed/chatbot" element={<EmbedChatbot />} />
          <Route path="/export/datastreams" element={<ProtectedRoute><ExportDatastreams /></ProtectedRoute>} />
          <Route path="/catalogus" element={<ProtectedRoute><Catalog /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/thanks" element={<ProtectedRoute><Thanks /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/aanvragen" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/bedrijfsgegevens" element={<ProtectedRoute><CompanyDetails /></ProtectedRoute>} />
          <Route path="/api-documentatie" element={<ProtectedRoute><ApiDocumentation /></ProtectedRoute>} />
          <Route path="/ontwikkelaars" element={<ProtectedRoute><ForDevelopers /></ProtectedRoute>} />
          <Route path="/ontwikkelaars/datastromen" element={<ProtectedRoute><DevDatastreams /></ProtectedRoute>} />
          <Route path="/ontwikkelaars/documentatie" element={<ProtectedRoute><DevDocumentation /></ProtectedRoute>} />
          <Route path="/ontwikkelaars/prijscatalogus" element={<ProtectedRoute><PriceCatalog /></ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
        </Routes>
      </Suspense>
      {!isEmbedRoute && <ChatWidget />}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <TooltipProvider>
            <AppContent />
          </TooltipProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
